// The player page's reads (spec/dashboard.md section 11.2): the player with presence re-read every 30 s while the page is
// open and the tab visible (Q-N-8), a collection's keys, all keyed under the environment workspace's cache prefix so the
// shell's Save All re-reads them.
import { useEffect } from 'react';
import { ops } from '../api/operations';
import type { Player, SaveCollectionDetail } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useGame } from '../shell/GameContext';
import { queryCache, useQuery, type QueryState } from '../workspace/cache';

export const PRESENCE_POLL_MS = 30_000;

/** `branch:<game id>:env:<slug>:player:<id>` — the prefix of every read of one player. */
export function playerPrefix(gameId: string, env: string, player: string): string {
  return `branch:${gameId}:env:${env}:player:${player}`;
}

/** Re-reads `key` every 30 s while mounted and the tab visible; a tab shown again after a long absence re-reads at once. */
export function usePresencePolling(key: string | null, enabled = true): void {
  useEffect(() => {
    if (!key || !enabled || typeof document === 'undefined') return;
    const visible = () => document.visibilityState !== 'hidden';
    let hiddenSince: number | null = null;
    const timer = setInterval(() => {
      if (visible()) queryCache.invalidate(key);
    }, PRESENCE_POLL_MS);
    const onVisibility = () => {
      if (visible()) {
        if (hiddenSince !== null && Date.now() - hiddenSince >= PRESENCE_POLL_MS) queryCache.invalidate(key);
        hiddenSince = null;
      } else hiddenSince = Date.now();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [key, enabled]);
}

/** The player (bindings, presence, sessions, collections) with the presence poll. */
export function usePlayer(env: string, playerId: string): QueryState<Player> {
  const { client } = useAuth();
  const { org, game } = useGame();
  const key = playerId ? `${playerPrefix(game.id, env, playerId)}:player` : null;
  const q = useQuery(key, () => client.data(ops.getPlayer, { path: { org, game: game.slug, env, player: playerId } }) as Promise<Player>);
  usePresencePolling(key, !q.error);
  return q;
}

/** One collection's current keys with their parsed values (section 16.2). */
export function useCollection(env: string, playerId: string, collection: string | null): QueryState<SaveCollectionDetail> {
  const { client } = useAuth();
  const { org, game } = useGame();
  const key = playerId && collection ? `${playerPrefix(game.id, env, playerId)}:collection:${collection}` : null;
  return useQuery(key, () => client.data(ops.getPlayerCollection, { path: { org, game: game.slug, env, player: playerId, collection: collection! } }) as Promise<SaveCollectionDetail>);
}

/** After a command (restore, reset) the player's reads are stale: drop them all. */
export function invalidatePlayer(gameId: string, env: string, playerId: string): void {
  queryCache.invalidate(playerPrefix(gameId, env, playerId));
}
